// Browser-side marker occlusion via a precomputed voxel-occupancy grid.
//
// The conversion service bakes a coarse occupancy grid per point cloud
// (occupancy.bin, see tools/occupancy_grid.py). Smart View fetches every file's
// grid, unions them, and raycasts marker line-of-sight against the union — so a
// tour/note/measurement marker is hidden when point-cloud geometry sits between
// the viewer's station and the marker. This is instant, smooth, cross-file and
// load-independent, replacing the noisy live GPU-pick occlusion.

export interface OccGrid { voxel: number; coords: Int32Array; }   // absolute world voxel coords (x,y,z each)
export interface OccIndex { voxel: number; occ: Set<number>; minx: number; miny: number; minz: number; }
interface V3 { x: number; y: number; z: number; }

const B = 1 << 17;          // per-axis span for the packed key (fits float64: 3 * 17 bits < 53)
const SKIP = 0.5;           // m — ignore geometry within this of either endpoint (the scanner's own artefacts).
                            // Small on purpose: a wall in the next room is often ~1–2 m from a station, so a
                            // large skip would step right over it (the "visible through the wall" bug).
const SKIP_FRAC = 0.4;      // …but never skip more than this fraction of a short ray, or nothing gets checked.
const OCC_MIN = 2;          // distinct occupied voxels between endpoints ⇒ occluded. Eye-level rays pass over low
                            // furniture, so a hit here is a wall/column/counter; 2 rejects a lone stray voxel.

/** Parse an OCC1 occupancy blob. Returns null if it isn't one (e.g. a 404 body). */
export function parseOccupancy(buf: ArrayBuffer): OccGrid | null {
  if (buf.byteLength < 12) return null;
  const dv = new DataView(buf);
  if (dv.getUint8(0) !== 0x4f || dv.getUint8(1) !== 0x43 || dv.getUint8(2) !== 0x43 || dv.getUint8(3) !== 0x31) return null; // "OCC1"
  const voxel = dv.getFloat32(4, true);
  const count = dv.getUint32(8, true);
  if (!voxel || count * 12 + 12 > buf.byteLength) return null;
  return { voxel, coords: new Int32Array(buf.slice(12, 12 + count * 12)) };
}

/** Union several files' grids into one queryable index (all grids share the voxel size). */
export function buildOccIndex(grids: OccGrid[]): OccIndex | null {
  const valid = grids.filter((g) => g && g.coords.length);
  if (!valid.length) return null;
  const voxel = valid[0].voxel;
  let minx = Infinity, miny = Infinity, minz = Infinity;
  for (const g of valid) {
    for (let i = 0; i < g.coords.length; i += 3) {
      if (g.coords[i] < minx) minx = g.coords[i];
      if (g.coords[i + 1] < miny) miny = g.coords[i + 1];
      if (g.coords[i + 2] < minz) minz = g.coords[i + 2];
    }
  }
  const occ = new Set<number>();
  for (const g of valid) {
    for (let i = 0; i < g.coords.length; i += 3) {
      occ.add(((g.coords[i] - minx) * B + (g.coords[i + 1] - miny)) * B + (g.coords[i + 2] - minz));
    }
  }
  return { voxel, occ, minx, miny, minz };
}

/** True if point-cloud geometry occludes the segment a→b (skipping a radius around each end). */
export function isOccluded(ix: OccIndex, a: V3, b: V3): boolean {
  const { voxel, occ, minx, miny, minz } = ix;
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z;
  const d = Math.hypot(dx, dy, dz);
  const step = voxel * 0.5;
  const n = Math.floor(d / step);
  if (n < 3) return false;
  const skip = Math.min(SKIP, d * SKIP_FRAC);   // clear scanner artefacts, but always check a short ray's middle
  const hit = new Set<number>();
  for (let i = 1; i < n; i++) {
    const t = i / n;
    const px = a.x + dx * t, py = a.y + dy * t, pz = a.z + dz * t;
    if (Math.hypot(px - a.x, py - a.y, pz - a.z) < skip) continue;
    if (Math.hypot(px - b.x, py - b.y, pz - b.z) < skip) continue;
    const k = ((Math.floor(px / voxel) - minx) * B + (Math.floor(py / voxel) - miny)) * B + (Math.floor(pz / voxel) - minz);
    if (occ.has(k)) { hit.add(k); if (hit.size >= OCC_MIN) return true; }
  }
  return false;
}

/**
 * Floor height under a scan station, from the grid. Walking down from the layer
 * under the scanner's own, the first layer that is a continuous surface: present
 * in `solid` of the columns within `radius` that hold anything in reach. The layer
 * right under the scanner must be `underScanner` complete — the tripod, the
 * operator and floor points spilling over a voxel boundary live there, and a short
 * tripod on a 0.3 m grid leaves only that one layer between head and floor.
 * Furniture tops and a raised floor's cable trays never reach `solid`, a hole in
 * the floor stays a minority, and a scan taken from inside a raised floor gets the
 * sub-floor while one standing on the tiles gets the tiles (verified on a data
 * hall: 171 stations, 91 of them under the floor). Null when nothing continuous
 * lies within `maxDrop` (a station at the scan's edge) — the caller falls back to
 * a fixed scanner height.
 */
export function floorBelow(
  ix: OccIndex, pos: V3,
  { radius = 1.5, maxDrop = 3.5, minColumns = 6, solid = 0.75, underScanner = 0.95 } = {},
): number | null {
  const { voxel, occ, minx, miny, minz } = ix;
  const cx = Math.floor(pos.x / voxel), cy = Math.floor(pos.y / voxel);
  const r = Math.ceil(radius / voxel);
  const kTop = Math.floor(pos.z / voxel) - 1;
  const kBot = Math.max(minz, Math.floor((pos.z - maxDrop) / voxel));
  if (kTop < kBot) return null;
  const columns: number[] = [];
  for (let i = Math.max(minx, cx - r); i <= cx + r; i++) {
    for (let j = Math.max(miny, cy - r); j <= cy + r; j++) {
      if (Math.hypot((i + 0.5) * voxel - pos.x, (j + 0.5) * voxel - pos.y) > radius) continue;
      const column = ((i - minx) * B + (j - miny)) * B - minz;
      for (let k = kBot; k <= kTop; k++) {
        if (occ.has(column + k)) { columns.push(column); break; }
      }
    }
  }
  if (columns.length < minColumns) return null;
  for (let k = kTop; k >= kBot; k--) {
    let n = 0;
    for (const column of columns) if (occ.has(column + k)) n++;
    if (n / columns.length >= (k === kTop ? underScanner : solid)) return (k + 0.5) * voxel;
  }
  return null;
}

/**
 * True when the floor spot of a tour ring is out of sight from a station. A WALL
 * crosses the line from the eye to a point `lift` above the ring: a column
 * occupied from the crossing up to `wallHeight` above the floor of the storey it
 * stands in (the viewer's, or the ring's for a crossing in the ring's storey) AND
 * flanked by two such columns on opposite sides (a wall, a rack row, a cage),
 * outside the target station's own tripod (`own` around the ring). For a ring
 * below the viewer's floor, the viewer's own floor crossed anywhere hides it (a
 * scan from inside a raised floor shows only through a lifted tile). For a ring
 * a STOREY or more away (`storey`), a floor crossed between the two hides it too:
 * a voxel with three of its four horizontal neighbours occupied, under the
 * viewer's floor down to the ring's, or from above the viewer's head up to and
 * including the ring's own floor — so a tower's floors show only through a hatch
 * or a stairwell. What never hides a ring: low clutter, a barrier, a lone pillar,
 * and the ghosts of people — a merged cloud of 171 scans held the workers at
 * hundreds of places, each a 1.8 m blob or a trail of them, which a plain "tall
 * column" test took for walls across open floor. The ring's own floor layer is
 * ignored on its storey. The eye-level test (`isOccluded`) hid nearly every ring:
 * at a short tripod's height a construction site is cluttered everywhere.
 */
export function ringOccluded(
  ix: OccIndex, eye: V3, eyeFloorZ: number, ring: V3, ringFloorZ: number,
  { lift = 0.4, wallHeight = 2.1, own = 0.6, storey = 1.5 } = {},
): boolean {
  const { voxel, occ, minx, miny, minz } = ix;
  const key = (i: number, j: number, k: number) => ((i - minx) * B + (j - miny)) * B + (k - minz);
  const b = { x: ring.x, y: ring.y, z: ringFloorZ + lift };
  const eyeK = Math.floor(eyeFloorZ / voxel), ringK = Math.floor(ringFloorZ / voxel);
  const below = ringK < eyeK, above = ringK > eyeK;
  const otherStorey = Math.abs(ringFloorZ - eyeFloorZ) >= storey;
  const wallTop = (z: number) => {
    const base = (below && z < eyeFloorZ) || (above && z >= ringFloorZ) ? ringFloorZ : eyeFloorZ;
    return Math.floor((base + wallHeight) / voxel);
  };
  const tallTo = (i: number, j: number, k: number, top: number) => {
    if (k > top) return false;
    for (let kk = k; kk <= top; kk++) if (!occ.has(key(i, j, kk))) return false;
    return true;
  };
  const slab = (i: number, j: number, k: number) =>
    (occ.has(key(i - 1, j, k)) ? 1 : 0) + (occ.has(key(i + 1, j, k)) ? 1 : 0)
    + (occ.has(key(i, j - 1, k)) ? 1 : 0) + (occ.has(key(i, j + 1, k)) ? 1 : 0) >= 3;
  const dx = b.x - eye.x, dy = b.y - eye.y, dz = b.z - eye.z;
  const d = Math.hypot(dx, dy, dz);
  const n = Math.floor(d / (voxel * 0.5));
  if (n < 3) return false;
  const skip = Math.min(SKIP, d * SKIP_FRAC);
  for (let s = 1; s < n; s++) {
    const t = s / n;
    const px = eye.x + dx * t, py = eye.y + dy * t, pz = eye.z + dz * t;
    if (Math.hypot(px - eye.x, py - eye.y, pz - eye.z) < skip) continue;
    const ci = Math.floor(px / voxel), cj = Math.floor(py / voxel), ck = Math.floor(pz / voxel);
    if (!occ.has(key(ci, cj, ck))) continue;
    if (below && ck === eyeK) return true;
    if (otherStorey && slab(ci, cj, ck)) {
      if (below && ck > ringK + 1 && ck < eyeK) return true;
      if (above && ck >= eyeK + 2 && ck <= ringK) return true;
    }
    if (ck === ringK) continue;
    if (Math.hypot(px - ring.x, py - ring.y) < own) continue;
    const top = wallTop(pz);
    if (!tallTo(ci, cj, ck, top)) continue;
    for (const [a, c] of OPPOSITE) {
      if (tallTo(ci + a[0], cj + a[1], ck, top) && tallTo(ci + c[0], cj + c[1], ck, top)) return true;
    }
  }
  return false;
}

const OPPOSITE: Array<[[number, number], [number, number]]> = [
  [[-1, 0], [1, 0]], [[0, -1], [0, 1]], [[-1, -1], [1, 1]], [[-1, 1], [1, -1]],
];
